// hooks/useProducts.js - With caching
import { useState, useEffect } from 'react';
import { dbOnValue, dbPush, dbUpdate, dbRemove } from '../config/firebase';
import { uploadImage, deleteImage, getProperImageUrl } from '../config/appwrite';
import { dataCache, getCachedData } from '../contexts/DataCache';

const PRODUCTS_CACHE_KEY = 'cached_products';

export const useProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadedFromCache, setLoadedFromCache] = useState(false);

  // Load products from Firebase with cache
  useEffect(() => {
    let isMounted = true;

    const loadProducts = async () => {
      // Try to load from cache first
      const cachedData = dataCache.get(PRODUCTS_CACHE_KEY);
      if (cachedData && Array.isArray(cachedData) && cachedData.length > 0) {
        console.log('[useProducts] Loaded from cache:', cachedData.length, 'products');
        if (isMounted) {
          setProducts(cachedData);
          setLoading(false);
          setLoadedFromCache(true);
        }
      }

      // Set up Firebase listener for updates (only for changes, not full reload)
      const unsubscribe = dbOnValue('products', (snapshot) => {
        const data = snapshot.val();
        let productList = [];
        
        if (data) {
          productList = Object.entries(data).map(([key, product]) => ({
            id: key,
            firebaseKey: key,
            ...product,
            inStock: product.stock !== false && product.outOfStock !== true,
            image: getProperImageUrl(product.image) || product.image,
            variants: product.variants?.map(v => ({
              ...v,
              images: v.images?.map(img => ({ ...img, url: getProperImageUrl(img.url) || img.url }))
            })) || []
          }));
          
          // Update cache
          dataCache.set(PRODUCTS_CACHE_KEY, productList);
        } else {
          productList = [];
          dataCache.set(PRODUCTS_CACHE_KEY, []);
        }
        
        if (isMounted) {
          setProducts(productList);
          setLoading(false);
        }
      });

      return unsubscribe;
    };

    loadProducts().then(unsubscribe => {
      // Store unsubscribe function for cleanup
      return () => {
        isMounted = false;
        if (unsubscribe) unsubscribe();
      };
    }).catch(error => {
      console.error('[useProducts] Error loading:', error);
      if (isMounted) {
        setLoading(false);
      }
    });

    // Cleanup
    return () => {
      isMounted = false;
    };
  }, []);

  // Add product
  const addProduct = async (productData) => {
    try {
      const dataWithStock = {
        ...productData,
        stock: true,
        outOfStock: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const cleanData = JSON.parse(JSON.stringify(dataWithStock));
      const result = await dbPush('products', cleanData);
      
      // Clear cache to force refresh
      dataCache.clear(PRODUCTS_CACHE_KEY);
      
      return { id: result.key, ...cleanData };
    } catch (error) {
      console.error('Add product error:', error);
      throw error;
    }
  };

  // Update product
  const updateProduct = async (productId, productData) => {
    try {
      const dataWithUpdate = {
        ...productData,
        updatedAt: new Date().toISOString()
      };
      const cleanData = JSON.parse(JSON.stringify(dataWithUpdate));
      await dbUpdate(`products/${productId}`, cleanData);
      
      // Clear cache to force refresh
      dataCache.clear(PRODUCTS_CACHE_KEY);
      
      return true;
    } catch (error) {
      console.error('Update product error:', error);
      throw error;
    }
  };

  // Delete product
  const deleteProduct = async (productId, imageId) => {
    try {
      if (imageId) {
        await deleteImage(imageId);
      }
      await dbRemove(`products/${productId}`);
      
      // Clear cache to force refresh
      dataCache.clear(PRODUCTS_CACHE_KEY);
      
      return true;
    } catch (error) {
      console.error('Delete product error:', error);
      throw error;
    }
  };

  // Toggle product stock status
  const toggleProductStock = async (productId, currentStockStatus) => {
    try {
      const newStockStatus = !currentStockStatus;
      await dbUpdate(`products/${productId}`, {
        stock: newStockStatus,
        outOfStock: !newStockStatus,
        updatedAt: new Date().toISOString()
      });
      
      // Clear cache to force refresh
      dataCache.clear(PRODUCTS_CACHE_KEY);
      
      return { success: true, inStock: newStockStatus };
    } catch (error) {
      console.error('Toggle stock error:', error);
      throw error;
    }
  };

  // Get only in-stock products
  const getInStockProducts = () => {
    return products.filter(product => product.inStock === true);
  };

  // Get product by ID
  const getProductById = (id, includeOutOfStock = false) => {
    const product = products.find(p => p.id === id || p.firebaseKey === id);
    if (!product) return null;
    if (!includeOutOfStock && !product.inStock) return null;
    return product;
  };

  // Get products by occasion
  const getProductsByOccasion = (occasion, includeOutOfStock = false) => {
    let filtered = products.filter(p => p.occasion === occasion || p.tag === occasion);
    if (!includeOutOfStock) {
      filtered = filtered.filter(p => p.inStock === true);
    }
    return filtered;
  };

  // Get all products
  const getAllProducts = () => {
    return products;
  };

  // Force refresh
  const refreshProducts = () => {
    dataCache.clear(PRODUCTS_CACHE_KEY);
    setLoading(true);
    // The listener will update automatically
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  return { 
    products,
    loading,
    loadedFromCache,
    addProduct,
    updateProduct,
    deleteProduct,
    toggleProductStock,
    getInStockProducts,
    getProductById,
    getProductsByOccasion,
    getAllProducts,
    refreshProducts
  };
};